import { API_BASE_URL } from '../api';
import type {
  PromoCode,
  PromoCodeCreate,
  PromoCodeUpdate,
  PromoCodeValidationResponse,
  PromoCodeUsage,
} from '../types/promoCode';

const headers = { 'Content-Type': 'application/json' };

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail = json.detail || text;
    } catch { /* keep raw text */ }
    throw new Error(detail);
  }
  return res.json();
}

export const promoCodeService = {
  // ====== PUBLIC ======
  async validate(code: string): Promise<PromoCodeValidationResponse> {
    const res = await fetch(`${API_BASE_URL}/api/promo-codes/validate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ code }),
    });
    return handleResponse<PromoCodeValidationResponse>(res);
  },

  // ====== BACKOFFICE ======
  async list(): Promise<PromoCode[]> {
    const res = await fetch(`${API_BASE_URL}/api/backoffice/promo-codes`, {
      credentials: 'include',
    });
    return handleResponse<PromoCode[]>(res);
  },

  async create(data: PromoCodeCreate): Promise<PromoCode> {
    const res = await fetch(`${API_BASE_URL}/api/backoffice/promo-codes`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<PromoCode>(res);
  },

  async update(id: number, data: PromoCodeUpdate): Promise<PromoCode> {
    const res = await fetch(`${API_BASE_URL}/api/backoffice/promo-codes/${id}`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<PromoCode>(res);
  },

  async delete(id: number): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/api/backoffice/promo-codes/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text);
    }
  },

  async getUsages(id: number): Promise<PromoCodeUsage[]> {
    const res = await fetch(`${API_BASE_URL}/api/backoffice/promo-codes/${id}/usages`, {
      credentials: 'include',
    });
    return handleResponse<PromoCodeUsage[]>(res);
  },
};
